import { faFile, faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Link } from 'react-router-dom';

import RemitServiceLineCard from '@/components/RemitServiceLineCard/RemitServiceLineCard';
import useAPI from '@/hooks/useAPI';
import RemitClaimRepository from '@/repositories/RemitClaimRepository';
import { RemitServiceLine } from '@/types/billing';
import Card from '@/ui/components/Card';
import Heading from '@/ui/components/Heading';
import Tooltip from '@/ui/components/Tooltip';

import Debug from '../Debug';

import type { Props } from './types';

const BillingItemRemitDetails = ({ billingItem }: Props) => {
  const claimRepo = RemitClaimRepository();

  const cms1500Ids: string[] =
    billingItem?.healthie_claim_rows
      ?.map((claimRow) => String(claimRow.cms1500?.healthie_id))
      .filter((id): id is string => !!id) || [];

  const { data } = useAPI({
    queryKey: ['claims', cms1500Ids],
    queryFn: async () => {
      const responses = await Promise.all(
        cms1500Ids.map((id) => claimRepo.listRemitClaims({ patient_control_number: id }))
      );
      return responses.map((response) => response.data?.results).flat();
    },
    enabled: cms1500Ids.length > 0
  });

  const claims =
    (data || [])
      .map((claim) => ({
        ...claim,
        service_lines: (claim?.service_lines || []).filter((serviceLine) => {
          const serviceLineCptCodes = (serviceLine?.claim_rows || []).map(
            (el) => el.cms1500_cpt_code_id
          );
          const billingItemCptCodes = billingItem.healthie_claim_rows.map((el) => el.id);
          return serviceLineCptCodes.some((code) => billingItemCptCodes.includes(code));
        })
      }))
      .sort(
        (a, b) =>
          new Date(b.financial_info?.paid_at || 0).getTime() -
          new Date(a.financial_info?.paid_at || 0).getTime() +
          (a?.payer_classification === 'unspecified' ? 1 : -1)
      ) || [];

  return (
    <Card className="p-2">
      <Heading text="Remit Details" value={4} />
      {claims.length === 0 && <p>No remits found</p>}
      {claims.map((claim) => (
        <div key={claim.id}>
          {claim.service_lines.length === 0 && <p>No service lines linked for this claim</p>}
          {claim.service_lines.map((serviceLine: RemitServiceLine) => (
            <div key={serviceLine.id}>
              <p className="text-lg flex items-center gap-2">
                <Link
                  to={`/billing/remit/${claim.remit_id}/edit?highlight_service_line_id=${serviceLine.id}`}
                  className="text-blue-600 whitespace-nowrap">
                  {claim.remit_id}
                  <Tooltip tooltipContent={claim.remit?.type == 'paper' ? 'Paper' : 'Electronic'}>
                    {claim.remit?.type == 'paper' ? (
                      <FontAwesomeIcon icon={faFile} className="pl-2" />
                    ) : (
                      <FontAwesomeIcon icon={faBoltLightning} className="pl-2" />
                    )}
                  </Tooltip>
                </Link>
                On:{' '}
                {claim.financial_info?.paid_at
                  ? new Date(claim.financial_info?.paid_at).toDateString()
                  : '-'}
                <span>From: {claim.financial_info?.payer_name}</span>
              </p>
              <RemitServiceLineCard serviceLine={serviceLine} editable={false} />
            </div>
          ))}
        </div>
      ))}
      <Debug object={billingItem} />
    </Card>
  );
};

export default React.memo(BillingItemRemitDetails);
